import { Box, Divider, Grid, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { useState } from "react";
import { connect } from "react-redux";
import { DekoxIcon, ImageCountBadge, ImageGalleryModal } from "src/components";
import { useProductInfo } from "src/pages/product-details/useProductInfo";
import { RootState } from "src/store";
import { useProjectsPageProductDetailsSectionT } from "src/translation-hooks/projects-page-product-details-section";
import dummyPicture from "src/assets/images/picture.png";
import parse from "html-react-parser";

const mapStateToProps = (state: RootState) => ({
  addEditConfigurationDialog: state.pageReducer.combinedConfiguratorReducer.addEditConfigurationDialogReducer.addEditConfigurationDialog
});

type Props = ReturnType<typeof mapStateToProps>;

const ProductDetailsBase = (props: Props) => {
  const { addEditConfigurationDialog } = props;

  const productId = addEditConfigurationDialog?.step === "PRODUCT-DETAILS" ? addEditConfigurationDialog.productId : undefined;

  const theme = useTheme();
  const t = useProjectsPageProductDetailsSectionT();
  const [open, setOpen] = useState(false);

  const { isLoadingImages, primaryPicture, pictures, currentIndex, product, setCurrentIndex } = useProductInfo({ productId });

  const handleOpen = (productPictureId: string) => {
    if (productPictureId) {
      setOpen(true);
      const index = pictures.findIndex(x => x.productPictureId === productPictureId);
      setCurrentIndex(index >= 0 ? index : 0);
    }
  };

  if (!product)
    return (
      <Stack direction="row" gap={2}>
        <Stack width={340}>
          <Skeleton variant="rectangular" height={290} />
        </Stack>
        <Stack flex={1} gap={2}>
          <Skeleton height={40} />
          <Skeleton height={30} />
          <Skeleton height={30} />
          <Skeleton height={30} />
          <Skeleton height={30} />
        </Stack>
      </Stack>
    );

  return (
    <>
      <Stack direction="row" gap={3}>
        {/* Left — images */}
        <Stack width={340} flexShrink={0}>
          {isLoadingImages ? (
            <Skeleton variant="rectangular" height={290} />
          ) : (
            <Box sx={{ cursor: pictures.length > 0 ? "pointer" : "default", position: "relative" }}>
              {pictures.length > 0 && (
                <ImageCountBadge>
                  <DekoxIcon icon="imageOutlined" sx={{ fontSize: 18 }} />
                  <Typography variant="body2" sx={{ fontSize: 14 }}>
                    {pictures.length}
                  </Typography>
                </ImageCountBadge>
              )}
              <img
                src={primaryPicture ? primaryPicture.picture : dummyPicture}
                alt="product"
                width="100%"
                height={290}
                onClick={() => handleOpen(primaryPicture?.productPictureId || pictures?.[0]?.productPictureId)}
                style={{ objectFit: "cover" }}
              />
              <Grid container spacing={1} sx={{ mt: 0 }}>
                {pictures
                  .filter(p => !p.isPrimary)
                  .slice(0, 4)
                  .map(picture => (
                    <Grid size={3} key={picture.productPictureId}>
                      <img
                        src={picture.picture}
                        alt={`product-${picture.productPictureId}`}
                        width="100%"
                        height={80}
                        onClick={() => handleOpen(picture.productPictureId)}
                        style={{ objectFit: "cover", cursor: "pointer" }}
                      />
                    </Grid>
                  ))}
              </Grid>
            </Box>
          )}
        </Stack>

        {/* Right — details */}
        <Stack flex={1} gap={0}>
          <Typography variant="h2" color="customColors.darkGrey" mb={2}>
            {product.description}
          </Typography>

          <Divider />

          <Stack gap={0} divider={<Divider />}>
            <Stack direction="row" gap={1.5} py={1.5} alignItems="flex-start">
              <DekoxIcon icon="category" sx={{ color: theme.palette.customColors.darkGrey, mt: 0.2 }} />
              <Stack>
                <Typography variant="subtitle2" color="customColors.darkGrey">
                  {t.productCategory}
                </Typography>
                <Typography variant="subtitle1" color="customColors.darkGrey">
                  {product.productTypeName || "-"}
                </Typography>
              </Stack>
            </Stack>

            <Stack direction="row" gap={1.5} py={1.5} alignItems="flex-start">
              <DekoxIcon icon="expand" sx={{ color: theme.palette.customColors.darkGrey, mt: 0.2 }} />
              <Stack>
                <Typography variant="subtitle2" color="customColors.darkGrey">
                  {t.width}
                </Typography>
                <Typography variant="subtitle1" color="customColors.darkGrey">
                  {product.productGroupCodeDescription || "-"}
                </Typography>
              </Stack>
            </Stack>

            <Stack direction="row" gap={1.5} py={1.5} alignItems="flex-start">
              <DekoxIcon icon="info" sx={{ color: theme.palette.customColors.darkGrey, mt: 0.2 }} />
              <Stack>
                <Typography variant="subtitle2" color="customColors.darkGrey">
                  {t.sound}
                </Typography>
                <Typography variant="subtitle1" color="customColors.darkGrey">
                  {product.productCategoryCodeDescription || "-"}
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          {product.summary && (
            <Stack gap={0.5} mt={1}>
              <Divider />
              <Stack py={1.5} gap={0.5}>
                <Typography variant="subtitle2" color="customColors.darkGrey">
                  {t.productDescription}
                </Typography>
                <Typography variant="subtitle1" color="customColors.darkGrey" lineHeight={1.6}>
                  {parse(product.summary)}
                </Typography>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Stack>

      <ImageGalleryModal open={open} images={pictures.map(p => p.picture)} currentIndex={currentIndex} setOpen={setOpen} setCurrentIndex={setCurrentIndex} />
    </>
  );
};

export const ProductDetails = connect(mapStateToProps)(ProductDetailsBase);
